import { setMockPianoConfig } from 'services/Piano';
import { setHeadData } from 'utils/metaDataUtils/headData';
import HubPage from 'pages/Hub/HubPage';
import type { Hub } from 'interfaces/Home';
import getSection from 'api/Section/getSection';
import type { GetStaticProps } from 'next/types';
import { layoutCount, Microsites, topicNavBorderColor } from 'constants/constants';
import MostPopularRR from 'components/MostPopularRR';
import type { ArticlePreview } from 'interfaces/content/articles/Articles';
import getMostPopularPosts from 'api/MostPopular/getMostPopular';
import SidebarContentPreview from 'components/SidebarContentPreview';
import setPageGtmConfig from 'services/Gtm/setPageGtmConfig';
import setWellSectionPageAdConfig from 'services/Ad/config/setWellSectionPageAdConfig';
import { omitKeysArray } from 'utils/objectUtils';
import type { Video } from 'interfaces/content/Media';
import { getVideosByCategoryFromPortal } from 'services/Video';
import getFooter from 'api/Footer/getFooter';
import type { HeaderNav, MenuItemProps } from 'interfaces/navigation/Header';
import { wellLogo } from 'icons/WellLogo';
import { getMicrositeConfig } from 'api/MicrositeConfig/getMicrositeConfig';
import handleStaticPropsError from 'utils/handleStaticPropsError';
import NotFoundError from 'interfaces/errors/NotFoundError';
import getMenu from 'api/Header/getMenu';
import { PageType } from 'interfaces/content/articles/Post';
import { calculatePagePagination, type PagePagination } from 'utils/miscUtils/calculatePageInfo';
import { safe, safeAsync } from 'utils/errorHandlingUtils';
import fallbackData from 'lib/fallbackData/commonData';

interface SectionProps {
  mostPopular: ArticlePreview[];
  videos: Video[];
  section: Hub;
  name: string;
  subMenu?: MenuItemProps[];
  pageSize: number;
  pagination: PagePagination;
}

const WellSectionHub = ({ section, mostPopular, name, videos, subMenu, pageSize, pagination }: SectionProps) => (
  <HubPage
    data={section}
    type={PageType.LATEST}
    pagination={pagination}
    sidebar={[
      mostPopular && (
        <MostPopularRR
          articles={mostPopular}
          key='1'
        />
      ),
      <SidebarContentPreview
        key='2'
        contentPlacementCD='right rail video'
        className='video-module'
        title='Videos'
        items={videos}
        type='video'
      />,
    ]}
    infiniteScroll={{
      numPostsPerPage: layoutCount.section,
      slug: name,
      taxonomy: 'section',
    }}
    isMicrosite
    subMenu={subMenu}
    color={topicNavBorderColor.well}
    pageSize={pageSize}
  />
);

export const getStaticProps: GetStaticProps = async (context) => {
  const pageSize = layoutCount.section;

  try {
    const { pageNum } = (context.params || {}) as { pageNum: string };
    const currentPageNum = parseInt(pageNum || '1', 10);

    if (Number.isNaN(currentPageNum) || currentPageNum < 1) {
      throw new NotFoundError(`[getHomePageNumber] invalid page number: '${pageNum}'`);
    }

    const footer = await safeAsync(() => getFooter(), fallbackData.footer);
    const menu: HeaderNav = await safeAsync(
      () => getMenu('LEFT_MENU_COM', 'RIGHT_MENU_COM', 'TOPIC_NAVIGATION_COM', Microsites.Well, wellLogo),
      fallbackData.headerNav,
    );
    const { name } = context.params as { name: string };

    const wellSections =
      (await safeAsync(() => getMicrositeConfig(Microsites.Well), fallbackData.pageData)).sections || {};
    if (Object.keys(wellSections).indexOf(name) === -1) throw new NotFoundError('Section not found');

    const section = await getSection(name, pageSize, (currentPageNum - 1) * pageSize, Microsites.Well, wellSections);
    section.posts = omitKeysArray(section.posts, ['eyebrowSectionLabel']);

    const videos = await safeAsync(() => getVideosByCategoryFromPortal(section.name), fallbackData.videos);

    const mostPopular = await safeAsync(() => getMostPopularPosts(), [] as ArticlePreview[]);

    const totalArticles = section.pageInfo.totalCount > 0 ? section.pageInfo.totalCount : 0;
    const pagination = calculatePagePagination(currentPageNum, totalArticles, pageSize);
    const pageAdConfig = safe(() => setWellSectionPageAdConfig(section), fallbackData.pageAdConfig);
    const dataLayer = safe(() => setPageGtmConfig({ ...section, platformCD: Microsites.Well }), fallbackData.dataLayer);

    const headData = safe(
      () =>
        setHeadData(
          {
            ...section,
            canonicalUrl: pageNum ? `${section.canonicalUrl}page/${pageNum}/` : section.canonicalUrl,
            platformCD: Microsites.Well,
            titlePrefix: pagination.pageInfo,
          },
          true,
          'Fortune Well',
        ),
      fallbackData.headData,
    );

    const subMenu = menu?.topicNavigation.find((menuItem) => menuItem.label === section.name)?.subMenu || null;

    return {
      props: {
        dataLayer,
        footer,
        headData,
        headerNav: menu,
        mostPopular,
        name,
        pageAdConfig,
        pageSize,
        pagination,
        pianoPageConfig: setMockPianoConfig(),
        section,
        subMenu,
        videos: videos?.videos,
      },
      revalidate: 60, // In seconds
    };
  } catch (error) {
    return handleStaticPropsError(error as Error);
  }
};

export const getStaticPaths = async () => {
  if (process.env.TEST) {
    return {
      fallback: 'blocking',
      paths: [{ params: { name: 'family' } }, { params: { name: 'family' } }],
    };
  }
  const { sections: wellSections } = await getMicrositeConfig(Microsites.Well);
  const paths = [
    ...Object.keys(wellSections).map((name) => ({ params: { name } })),
    ...Object.keys(wellSections).map((name) => ({ params: { name } })),
  ];

  return {
    fallback: 'blocking',
    paths,
  };
};

export default WellSectionHub;
